import React, {useState} from 'react';
import * as ReactDOM from 'react-dom';

import classNames from 'classnames';
import {usePopperTooltip} from 'react-popper-tooltip';
import {Placement} from '@popperjs/core';

import {Button} from '../../components/button';

export interface IDropdownProps {
    /**
     * Content.
     */
    children: React.ReactNode;
    /**
     * Should the dropdown be initially shown.
     */
    defaultOpen?: boolean;
    /**
     * Show dropdown with a delay.
     */
    delayShow?: number;
    /**
     * Whether to close the tooltip when its trigger is out of boundary.
     */
    isHidden?: boolean;
    /**
     * Open state if component is controlled.
     */
    isOpen?: boolean;
    /**
     * Additional classes.
     */
    className?: string;
    /**
     * Specify offset.
     */
    offset?: [number, number];
    /**
     * Callback.
     */
    onVisibleChange?: (visible: boolean) => void;
    /**
     * Preferred placement of the dropdown.
     */
    placement?: Placement;
    /**
     * Dropdown trigger element.
     */
    trigger?: IDropdownToggleProps;
    /**
     * Event to trigger dropdown. Default is 'click'.
     */
    triggerType?: TDropdownTrigger | TDropdownTrigger[];
}

export type TDropdownTrigger = 'click' | 'hover';

export interface IDropdownToggleProps {
    /**
     * Content.
     */
    children?: string | React.ReactNode;
    /**
     * Additional classes.
     */
    className?: string;
    /**
     * Trigger ref.
     */
    setTriggerRef?: React.Dispatch<React.SetStateAction<HTMLElement | null>>;
    onClick?: () => void;
}

const DropdownToggle = (props: IDropdownToggleProps): JSX.Element => {
    const className: string = classNames(
        'dropdown-toggle',
        props.className,
    );

    if (props.children) {
        return (
            <div
                className={className}
                ref={props.setTriggerRef}
                onClick={props.onClick}
            >
                <span className="dropdown-toggle__inner">
                    <span className="dropdown-toggle__text">{props.children}</span>
                </span>
            </div>
        );
    } else {
        return (
            <Button
                buttonRef={props.setTriggerRef}
                color='white'
                icon='more'
                text='Show more'
                type="button"
            />
        );
    }
};

const Dropdown = (props: IDropdownProps): JSX.Element => {
    const [isMounted, setIsMounted] = useState(false);

    const {
        getArrowProps,
        getTooltipProps,
        setTooltipRef,
        setTriggerRef,
        state,
        visible,
    } = usePopperTooltip({
        closeOnTriggerHidden: props.isHidden || false,
        defaultVisible: props.defaultOpen || false,
        delayShow: props.delayShow || 0,
        offset: props.offset ? props.offset : [-40, 17],
        onVisibleChange: props.onVisibleChange,
        placement: props.placement || 'top',
        trigger: props.triggerType || 'hover',
        visible: props.isOpen,
    });

    React.useEffect(() => {
        setIsMounted(true);

        return (): void => {
            setIsMounted(false);
        };
    }, []);

    const className: string = classNames(
        'dropdown',
        {
            [`dropdown--placement-${state && state.placement}`]: state && !!state.placement,
            'is-visible': visible,
        },
        props.className,
    );

    const renderDropdown: () => JSX.Element = () => (
        <div className={className} ref={setTooltipRef} {...getTooltipProps()}>
            <div className="dropdown__inner">
                <div className="dropdown__content">
                    {props.children}
                </div>
                <div {...getArrowProps()} className="dropdown__arrow">
                    <div className="dropdown__arrow-inner" />
                </div>
            </div>
        </div>
    );

    return (
        <React.Fragment>
            <DropdownToggle {...props.trigger} setTriggerRef={setTriggerRef} />
            {(isMounted && visible) ? ReactDOM.createPortal(renderDropdown(), document.body) : renderDropdown()}
        </React.Fragment>
    );
};

export default Dropdown;
